import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    Collapse,
    IconButton,
    LinearProgress,
    ListItemButton,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EmailIcon from '@mui/icons-material/Email';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import GroupsIcon from '@mui/icons-material/Groups';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import NoMeetingRoomIcon from '@mui/icons-material/NoMeetingRoom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import CommonButton from '../../components/atom/CommonButton';
import { formatDateTimeInVenueTz } from '../../helpers/formatDateTime';
import { normalizeHexColor } from './seatColorMap';
import { UNGROUPED_TABLE_KEY, compareTableEntries } from './seatTables';

const SCROLL_CONTAINER_SX = { overflow: 'auto', flex: 1, minHeight: 0 };
const LIST_ITEM_SX = {
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    px: 2,
    py: 1.5,
    transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
    '&:hover': {
        borderColor: 'primary.light',
    },
    '&.Mui-selected': {
        borderColor: 'primary.main',
        bgcolor: 'action.selected',
    },
};
const SECTION_CARD_SX = {
    bgcolor: 'background.paper',
    borderRadius: 1,
    p: 2,
    border: '1px solid',
    borderColor: 'divider',
};
const TAB_COUNT_CHIP_SX = {
    height: 20,
    minWidth: 20,
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '999px',
    '& .MuiChip-label': { px: 0.75 },
};
const SECTION_TABS_SX = {
    mb: 1.5,
    minHeight: 36,
    '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600 },
};
const TABLE_LABEL_SX = {
    fontWeight: 600,
    fontSize: '1.2rem',
    color: 'text.secondary',
    display: 'block',
    mt: 1.25,
    mb: 0.5,
};
const GROUP_COLORS = [
    '#1976d2',
    '#388e3c',
    '#f57c00',
    '#7b1fa2',
    '#c62828',
    '#0097a7',
    '#558b2f',
    '#e65100',
    '#4527a0',
    '#00695c',
];

function getGroupColor(groupId) {
    return GROUP_COLORS[(groupId - 1) % GROUP_COLORS.length];
}

function computeTicketTypeSummary(items, ticketTypeById) {
    const countByType = new Map();
    items.forEach((item) => {
        const ticketType = ticketTypeById?.[item.ticketTypeId];
        if (!ticketType || !Array.isArray(ticketType?.seats) || ticketType.seats.length === 0)
            return;
        const name = ticketType.name || `#${item.ticketTypeId}`;
        countByType.set(name, (countByType.get(name) || 0) + 1);
    });
    return Array.from(countByType.entries())
        .map(([name, count]) => `${count} x ${name}`)
        .join(', ');
}

function getMaxTicketTypePrice(ticketType) {
    const matrix = ticketType?.priceMatrix;
    if (!matrix || typeof matrix !== 'object') return 0;
    let max = 0;
    for (const v of Object.values(matrix)) {
        if (typeof v === 'number' && v > max) max = v;
    }
    return max;
}

function getPrimaryTicketType(group, ticketTypeById) {
    let best = null;
    let bestPrice = -1;
    group.items.forEach((item) => {
        const tt = ticketTypeById?.[item.ticketTypeId];
        if (!tt || !Array.isArray(tt?.seats) || tt.seats.length === 0) return;
        const price = getMaxTicketTypePrice(tt);
        if (price > bestPrice || !best) {
            best = tt;
            bestPrice = price;
        }
    });
    return best;
}

function groupByTicketType(groups, ticketTypeById) {
    const sections = new Map();
    groups.forEach((group) => {
        const primary = getPrimaryTicketType(group, ticketTypeById);
        const key = primary?.id ?? 0;
        if (!sections.has(key)) {
            sections.set(key, {
                ticketType: primary,
                price: primary ? getMaxTicketTypePrice(primary) : 0,
                groups: [],
            });
        }
        sections.get(key).groups.push(group);
    });
    return Array.from(sections.values()).sort((a, b) => b.price - a.price);
}

// Bookings without any per-(type, table) entry — e.g. a ticket type with no
// seats configured — collapse into a single UNGROUPED_TABLE_KEY bucket so they
// still appear under the Assigned tab instead of being silently dropped.
function buildTableEntries(assignedGroups, tableAssignmentsByBookingId, sectionTicketTypeId) {
    const byTable = new Map();
    const orphans = [];
    assignedGroups.forEach((group) => {
        const perTable =
            tableAssignmentsByBookingId?.[group.bookingId]?.[sectionTicketTypeId] || [];
        if (perTable.length === 0) {
            orphans.push(group);
            return;
        }
        const isSplit = perTable.length > 1;
        perTable.forEach(({ tableId, tableName, count }) => {
            if (!byTable.has(tableId)) {
                byTable.set(tableId, { tableId, tableName, entries: [] });
            }
            byTable.get(tableId).entries.push({ group, count, isSplit });
        });
    });
    const sorted = Array.from(byTable.values()).sort(compareTableEntries);
    if (orphans.length > 0) {
        sorted.push({
            tableId: UNGROUPED_TABLE_KEY,
            tableName: null,
            entries: orphans.map((group) => ({ group, count: group.pax, isSplit: false })),
        });
    }
    return sorted;
}

function BookingRow({
    group,
    excludedSeatIds = new Set(),
    mode,
    tableSeatCount,
    isSplit,
    isSelected,
    showMismatchIcon,
    showNotes,
    onToggleSelect,
    onForceSelect,
    ticketTypeById,
    timeslot,
    onClearSeats,
    onSendSeatingChangedEmail,
    sendingSeatingChangedBookingIds,
    delayedSeatAssignmentEnabled,
    hasCrossTypeMismatch,
    getDraftMissingCount,
    assignedSeats,
    onSuggest,
    onRemoveSeat,
    isSuggesting,
    isSaving,
    seatingGroupId,
    seatingGroupRequest,
    seatingGroupNote,
    groupLabel,
    groupColor,
    onConnect,
    onSeatGroup,
    onUnseatGroup,
    isGroupSeated,
    seatGroupLoading,
}) {
    const { t } = useTranslation();
    const ticketSummary = computeTicketTypeSummary(group.items, ticketTypeById);
    const draftMissing = getDraftMissingCount(group.bookingId);
    const missing = draftMissing ?? group.unassignedCount;
    const allSeated = missing === 0;
    const hasMismatch = hasCrossTypeMismatch(group.bookingId);
    const massEmailSent = !!timeslot?.seatingLockEmailSentAt;
    const canSendSeatingEmail = allSeated && !!group.booking?.email;
    const seatingChanged = !!group.booking?.seatingChangedSinceLastEmail;
    const name =
        `${group.booking?.firstName || ''} ${group.booking?.lastName || ''}`.trim() || null;
    const hasIndoorConflict =
        excludedSeatIds.size > 0 &&
        group.items.some((item) => item.ticketSeatId && excludedSeatIds.has(item.ticketSeatId));

    let countChip;
    if (mode === 'assignedTable') {
        countChip = (
            <Chip
                size="small"
                color="success"
                label={tableSeatCount}
                sx={{
                    fontWeight: 600,
                    minWidth: 32,
                    height: 24,
                    fontSize: '1.05rem',
                    letterSpacing: '0.05em',
                    borderRadius: '6px',
                }}
            />
        );
    } else {
        countChip = (
            <Chip
                size="small"
                color={allSeated ? 'success' : 'warning'}
                label={allSeated ? group.pax : `${group.pax - missing}/${group.pax}`}
                sx={{
                    fontWeight: 600,
                    minWidth: 32,
                    height: 24,
                    fontSize: '1.05rem',
                    letterSpacing: '0.05em',
                    borderRadius: '6px',
                }}
            />
        );
    }

    return (
        <Box>
            <ListItemButton
                selected={isSelected}
                onClick={() => onToggleSelect()}
                sx={
                    hasIndoorConflict
                        ? { ...LIST_ITEM_SX, borderColor: 'warning.main', borderWidth: 2 }
                        : LIST_ITEM_SX
                }
            >
                <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                        >
                            #{group.bookingId}
                            {group.booking?.createdAt && (
                                <>
                                    {' · '}
                                    {formatDateTimeInVenueTz(group.booking.createdAt)}
                                </>
                            )}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            {seatingGroupId ? (
                                <>
                                    <Tooltip title={t('admin.seatingGroup.connectButton')}>
                                        <Chip
                                            size="small"
                                            icon={<GroupsIcon style={{ fontSize: 13 }} />}
                                            label={groupLabel}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onConnect(
                                                    group.bookingId,
                                                    seatingGroupId,
                                                    seatingGroupNote,
                                                );
                                            }}
                                            sx={{
                                                height: 22,
                                                fontSize: '0.72rem',
                                                fontWeight: 700,
                                                bgcolor: groupColor,
                                                color: '#fff',
                                                '& .MuiChip-icon': { color: '#fff' },
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Tooltip>
                                    {isGroupSeated(seatingGroupId) ? (
                                        <Tooltip title={t('admin.seatingGroup.unseatGroup')}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onUnseatGroup(seatingGroupId);
                                                    }}
                                                    disabled={
                                                        seatGroupLoading === seatingGroupId ||
                                                        isSaving ||
                                                        isSuggesting
                                                    }
                                                    sx={{ p: 0.25 }}
                                                >
                                                    <NoMeetingRoomIcon
                                                        fontSize="small"
                                                        color="warning"
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title={t('admin.seatingGroup.seatGroup')}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onSeatGroup(seatingGroupId);
                                                    }}
                                                    disabled={
                                                        seatGroupLoading === seatingGroupId ||
                                                        isSaving ||
                                                        isSuggesting
                                                    }
                                                    sx={{ p: 0.25 }}
                                                >
                                                    <EventSeatIcon
                                                        fontSize="small"
                                                        color="primary"
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </>
                            ) : seatingGroupRequest ? (
                                <>
                                    <Tooltip
                                        title={`${t('admin.seatingGroup.wantsToSitWith')}: ${seatingGroupNote || '-'}`}
                                    >
                                        <GroupsIcon
                                            fontSize="small"
                                            sx={{ color: 'warning.main' }}
                                        />
                                    </Tooltip>
                                    <Tooltip title={t('admin.seatingGroup.connectButton')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onConnect(
                                                        group.bookingId,
                                                        null,
                                                        seatingGroupNote,
                                                    );
                                                }}
                                                sx={{ p: 0.25 }}
                                            >
                                                <GroupsIcon fontSize="small" color="action" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            ) : null}
                            {mode === 'assignedTable' && isSplit && (
                                <Tooltip title={t('timeslots.assignment.splitBookingTooltip')}>
                                    <CallSplitIcon fontSize="small" sx={{ color: 'info.main' }} />
                                </Tooltip>
                            )}
                            {delayedSeatAssignmentEnabled && missing < group.pax && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onForceSelect();
                                        onClearSeats(group.bookingId);
                                    }}
                                    title={t('timeslots.assignment.clearSeats')}
                                    sx={{ p: 0.25 }}
                                >
                                    <PersonRemoveIcon fontSize="small" />
                                </IconButton>
                            )}
                            {canSendSeatingEmail && (
                                <Tooltip
                                    title={t(
                                        !massEmailSent
                                            ? 'timeslots.assignment.sendSeatingEmail'
                                            : seatingChanged
                                              ? 'timeslots.assignment.resendSeatingChangedEmail'
                                              : 'timeslots.assignment.resendSeatingEmail',
                                    )}
                                >
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSendSeatingChangedEmail(group.bookingId);
                                            }}
                                            disabled={
                                                sendingSeatingChangedBookingIds?.has(
                                                    group.bookingId,
                                                ) ?? false
                                            }
                                            sx={{ p: 0.25 }}
                                        >
                                            {!massEmailSent || seatingChanged ? (
                                                <EmailIcon fontSize="small" color="primary" />
                                            ) : (
                                                <MarkEmailReadIcon
                                                    fontSize="small"
                                                    sx={{ color: 'text.disabled' }}
                                                />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            {hasMismatch && showMismatchIcon && (
                                <Tooltip title={t('timeslots.assignment.crossTypeMismatchTooltip')}>
                                    <ErrorOutlineIcon
                                        fontSize="small"
                                        sx={{ color: 'error.main' }}
                                    />
                                </Tooltip>
                            )}
                            {countChip}
                        </Stack>
                    </Stack>
                    {name && (
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {name}
                        </Typography>
                    )}
                    {(() => {
                        if (!showNotes) return null;
                        const notes = (group.booking?.note || []).filter((n) => n?.value);
                        const checkinNote = group.booking?.checkinNote;
                        if (notes.length === 0 && !checkinNote) return null;
                        return (
                            <>
                                {notes.length > 0 && (
                                    <Box
                                        sx={{
                                            mt: 0.5,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 0.5,
                                            bgcolor: '#fff8e1',
                                            border: '1px solid',
                                            borderColor: 'warning.light',
                                        }}
                                    >
                                        {notes.map((n, idx) => (
                                            <Typography
                                                key={`${n.label}-${idx}`}
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    lineHeight: 1.3,
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                <Box component="span" sx={{ fontWeight: 600 }}>
                                                    {n.label}:
                                                </Box>{' '}
                                                {n.value}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                                {checkinNote && (
                                    <Box
                                        sx={{
                                            mt: 0.5,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 0.5,
                                            bgcolor: '#e3f2fd',
                                            border: '1px solid',
                                            borderColor: 'info.light',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                lineHeight: 1.3,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            <Box component="span" sx={{ fontWeight: 600 }}>
                                                {t('bookings.columns.checkinNote')}:
                                            </Box>{' '}
                                            {checkinNote}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        );
                    })()}
                    {ticketSummary && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.3 }}
                        >
                            {ticketSummary}
                        </Typography>
                    )}
                    {hasIndoorConflict && (
                        <Alert
                            severity="warning"
                            icon={<WarningAmberIcon sx={{ fontSize: '1.5rem' }} />}
                            sx={{
                                mt: 1,
                                py: 0.5,
                                px: 1,
                                fontSize: '1rem',
                                fontWeight: 600,
                                alignItems: 'center',
                            }}
                        >
                            {t('timeslots.assignment.arrangementSeatConflict')}
                        </Alert>
                    )}
                </Box>
            </ListItemButton>
            <Collapse in={isSelected} unmountOnExit>
                <Box
                    sx={{
                        mt: 1,
                        px: 1.5,
                        py: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    {(assignedSeats || []).map((tableGroup) => (
                        <Box key={tableGroup.tableId}>
                            {tableGroup.tableName && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontWeight: 600,
                                        display: 'block',
                                        mb: 0.5,
                                        textAlign: 'left',
                                    }}
                                >
                                    {tableGroup.tableName}
                                </Typography>
                            )}
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {tableGroup.seats.map((seat) => (
                                    <Chip
                                        key={seat.id}
                                        label={seat.name}
                                        size="small"
                                        variant="outlined"
                                        onDelete={() => onRemoveSeat(seat.id)}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ))}
                    <CommonButton
                        onClick={onSuggest}
                        label={t('timeslots.assignment.suggest')}
                        buttonType="primary"
                        disabled={isSuggesting || isSaving}
                    />
                </Box>
            </Collapse>
        </Box>
    );
}

function BookingGroupList({
    bookingGroups,
    unassignedGroups,
    selectedBookingId,
    setSelectedBookingId,
    ticketTypeById,
    timeslot,
    onSendSeatingLockEmail,
    isSendingLockEmail,
    getDraftMissingCount,
    hasCrossTypeMismatch,
    onClearSeats,
    delayedSeatAssignmentEnabled,
    onSendSeatingChangedEmail,
    sendingSeatingChangedBookingIds,
    assignedSeats,
    tableAssignmentsByBookingId,
    onSuggest,
    onRemoveSeat,
    isSuggesting,
    isSaving,
    excludedSeatIds = new Set(),
    seatingGroupMeta,
    onConnect,
    onSeatGroup,
    onUnseatGroup,
    isGroupSeated,
    seatGroupLoading,
}) {
    const { t } = useTranslation();

    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDir, setSortDir] = useState('asc');
    const [showNotes, setShowNotes] = useState(true);
    const [selectedRowKey, setSelectedRowKey] = useState(null);

    const compareGroups = (a, b) => {
        let cmp = 0;
        if (sortBy === 'bookingId') {
            cmp = a.bookingId - b.bookingId;
        } else if (sortBy === 'name') {
            const nameA = `${a.booking?.firstName || ''} ${a.booking?.lastName || ''}`.trim();
            const nameB = `${b.booking?.firstName || ''} ${b.booking?.lastName || ''}`.trim();
            cmp = nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        } else {
            const tA = a.booking?.createdAt ? new Date(a.booking.createdAt).getTime() : 0;
            const tB = b.booking?.createdAt ? new Date(b.booking.createdAt).getTime() : 0;
            cmp = tA - tB;
        }
        return sortDir === 'asc' ? cmp : -cmp;
    };
    const sortGroups = (groups) => [...groups].sort(compareGroups);
    const sortEntries = (entries) => [...entries].sort((a, b) => compareGroups(a.group, b.group));

    // Build map: bookingId → { seatingGroupId, seatingGroupRequest, seatingGroupNote }
    const seatingGroupByBookingId = {};
    (bookingGroups || []).forEach((g) => {
        seatingGroupByBookingId[g.bookingId] = {
            seatingGroupId: g.booking?.seatingGroupId ?? null,
            seatingGroupRequest: g.booking?.seatingGroupRequest ?? false,
            seatingGroupNote: g.booking?.seatingGroupNote ?? null,
        };
    });

    const sections = groupByTicketType(bookingGroups || [], ticketTypeById);

    const [expandedSections, setExpandedSections] = useState(
        new Set(sections.map((s) => s.ticketType?.id ?? 'other')),
    );
    const toggleSection = (id) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const [sectionTab, setSectionTab] = useState({});
    const resolveSectionTab = (sectionId, hasUnassigned, hasAssigned) => {
        const stored = sectionTab[sectionId];
        if (stored === 'unassigned' && hasUnassigned) return 'unassigned';
        if (stored === 'assigned' && hasAssigned) return 'assigned';
        return hasUnassigned ? 'unassigned' : 'assigned';
    };
    const setSectionTabFor = (sectionId, value) =>
        setSectionTab((prev) => ({ ...prev, [sectionId]: value }));

    const allAssigned = unassignedGroups.length === 0;

    // selectedBookingId is set externally too (initial default, map clicks);
    // selectedRowKey is local-only so split bookings can deselect a single
    // row without toggling the whole booking off.
    const rowKeyOf = (group, mode, tableId) =>
        mode === 'assignedTable' ? `b${group.bookingId}:t${tableId}` : `b${group.bookingId}:u`;

    // Keep selectedRowKey in sync with selectedBookingId. When the parent
    // changes booking selection externally, default to the Unassigned row
    // if the booking has unseated items, else the first table row found.
    useEffect(() => {
        if (selectedBookingId == null) {
            if (selectedRowKey != null) setSelectedRowKey(null);
            return;
        }
        if (selectedRowKey && selectedRowKey.startsWith(`b${selectedBookingId}:`)) return;
        const hasUnassigned = (unassignedGroups || []).some(
            (g) => g.bookingId === selectedBookingId,
        );
        if (hasUnassigned) {
            setSelectedRowKey(`b${selectedBookingId}:u`);
            return;
        }
        const byType = tableAssignmentsByBookingId?.[selectedBookingId];
        if (byType) {
            for (const perTable of Object.values(byType)) {
                if (perTable && perTable.length > 0) {
                    setSelectedRowKey(`b${selectedBookingId}:t${perTable[0].tableId}`);
                    return;
                }
            }
        }
        setSelectedRowKey(null);
    }, [selectedBookingId, selectedRowKey, unassignedGroups, tableAssignmentsByBookingId]);

    if (!bookingGroups || bookingGroups.length === 0) {
        return (
            <Box>
                <Alert severity="info">{t('timeslots.assignment.noBookings')}</Alert>
            </Box>
        );
    }

    const toggleSelect = (group, mode, tableId) => {
        const rowKey = rowKeyOf(group, mode, tableId);
        if (rowKey === selectedRowKey) {
            setSelectedRowKey(null);
            setSelectedBookingId(null);
            return;
        }
        setSelectedRowKey(rowKey);
        setSelectedBookingId(group.bookingId);
    };
    const forceSelect = (group, mode, tableId) => {
        const rowKey = rowKeyOf(group, mode, tableId);
        setSelectedRowKey(rowKey);
        setSelectedBookingId(group.bookingId);
    };

    const renderRow = (group, mode, tableSeatCount, isSplit, tableKey, showMismatchIcon = true) => {
        const rowKey = rowKeyOf(group, mode, tableKey);
        const isRowSelected = rowKey === selectedRowKey;
        const sgInfo = seatingGroupByBookingId[group.bookingId] || {};
        // Resolve the per-timeslot label/color: a name when set, else "Group {ordinal}"
        // where ordinal is the group's 1-based position within this timeslot.
        const meta = sgInfo.seatingGroupId ? seatingGroupMeta?.[sgInfo.seatingGroupId] : null;
        const groupLabel = meta
            ? meta.name || t('admin.seatingGroup.badge', { n: meta.ordinal })
            : '';
        const groupColor = meta ? getGroupColor(meta.ordinal) : undefined;
        return (
            <BookingRow
                key={rowKey}
                group={group}
                excludedSeatIds={excludedSeatIds}
                mode={mode}
                tableSeatCount={tableSeatCount}
                isSplit={!!isSplit}
                isSelected={isRowSelected}
                showMismatchIcon={showMismatchIcon}
                showNotes={showNotes}
                onToggleSelect={() => toggleSelect(group, mode, tableKey)}
                onForceSelect={() => forceSelect(group, mode, tableKey)}
                ticketTypeById={ticketTypeById}
                timeslot={timeslot}
                onClearSeats={onClearSeats}
                onSendSeatingChangedEmail={onSendSeatingChangedEmail}
                sendingSeatingChangedBookingIds={sendingSeatingChangedBookingIds}
                delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                hasCrossTypeMismatch={hasCrossTypeMismatch}
                getDraftMissingCount={getDraftMissingCount}
                assignedSeats={isRowSelected ? assignedSeats : undefined}
                onSuggest={onSuggest}
                onRemoveSeat={onRemoveSeat}
                isSuggesting={isSuggesting}
                isSaving={isSaving}
                seatingGroupId={sgInfo.seatingGroupId}
                seatingGroupRequest={sgInfo.seatingGroupRequest}
                seatingGroupNote={sgInfo.seatingGroupNote}
                groupLabel={groupLabel}
                groupColor={groupColor}
                onConnect={onConnect}
                onSeatGroup={onSeatGroup}
                onUnseatGroup={onUnseatGroup}
                isGroupSeated={isGroupSeated}
                seatGroupLoading={seatGroupLoading}
            />
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: { xs: 'auto', md: '100%' },
                minHeight: 0,
            }}
        >
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexShrink: 0 }}>
                <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{ flex: 2, '& .MuiSelect-select': { textAlign: 'left' } }}
                >
                    <MenuItem value="createdAt">
                        {t('timeslots.assignment.sortByCreatedAt')}
                    </MenuItem>
                    <MenuItem value="bookingId">
                        {t('timeslots.assignment.sortByBookingId')}
                    </MenuItem>
                    <MenuItem value="name">{t('timeslots.assignment.sortByName')}</MenuItem>
                </Select>
                <Select
                    value={sortDir}
                    onChange={(e) => setSortDir(e.target.value)}
                    sx={{ flex: 1, '& .MuiSelect-select': { textAlign: 'left' } }}
                >
                    <MenuItem value="desc">{t('timeslots.assignment.sortDesc')}</MenuItem>
                    <MenuItem value="asc">{t('timeslots.assignment.sortAsc')}</MenuItem>
                </Select>
                <Tooltip
                    title={t(
                        showNotes
                            ? 'timeslots.assignment.hideNotes'
                            : 'timeslots.assignment.showNotes',
                    )}
                >
                    <IconButton
                        onClick={() => setShowNotes((v) => !v)}
                        color={showNotes ? 'primary' : 'default'}
                        sx={{ flexShrink: 0 }}
                    >
                        <StickyNote2Icon />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Box
                sx={{
                    ...SCROLL_CONTAINER_SX,
                    // On mobile the whole page scrolls, so this region flows to its content
                    // height; on desktop it becomes the bounded internal scroll area.
                    overflow: { xs: 'visible', md: 'auto' },
                    flex: { xs: 'none', md: 1 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                }}
            >
                {allAssigned && (
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack spacing={2}>
                            <Alert
                                severity="success"
                                sx={{
                                    py: 0.5,
                                    '& .MuiAlert-message': {
                                        color: 'grey.900',
                                        fontWeight: 600,
                                    },
                                }}
                            >
                                {t('timeslots.assignment.allAssigned')}
                            </Alert>
                            {isSendingLockEmail ? (
                                <Stack spacing={1}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('timeslots.assignment.sendingSeatingEmails')}
                                    </Typography>
                                    <LinearProgress />
                                </Stack>
                            ) : timeslot?.seatingLockEmailSentAt ? (
                                <Stack spacing={1.5}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('timeslots.assignment.seatingLockEmailSentOn', {
                                            date: formatDateTimeInVenueTz(
                                                timeslot.seatingLockEmailSentAt,
                                            ),
                                        })}
                                    </Typography>
                                    <CommonButton
                                        onClick={onSendSeatingLockEmail}
                                        label={t('timeslots.assignment.resendSeatingLockEmail')}
                                        buttonType="secondary"
                                        disabled={isSendingLockEmail}
                                    />
                                </Stack>
                            ) : (
                                <CommonButton
                                    onClick={onSendSeatingLockEmail}
                                    label={t('timeslots.assignment.sendSeatingLockEmail')}
                                    buttonType="primary"
                                    disabled={isSendingLockEmail}
                                />
                            )}
                        </Stack>
                    </Box>
                )}
                {sections.map((section) => {
                    const sectionId = section.ticketType?.id ?? 'other';
                    const sectionTicketTypeId = section.ticketType?.id ?? 0;
                    const isExpanded = expandedSections.has(sectionId);
                    const unassignedInSection = section.groups.filter((g) => g.unassignedCount > 0);
                    const assignedInSection = section.groups.filter((g) => g.unassignedCount === 0);
                    const unassignedPax = unassignedInSection.reduce((sum, g) => sum + g.pax, 0);
                    const assignedPax = assignedInSection.reduce((sum, g) => sum + g.pax, 0);
                    const tableEntries = buildTableEntries(
                        assignedInSection,
                        tableAssignmentsByBookingId,
                        sectionTicketTypeId,
                    );
                    const seenBookingsInAssigned = new Set();
                    const currentTab = resolveSectionTab(
                        sectionId,
                        unassignedInSection.length > 0,
                        assignedInSection.length > 0,
                    );
                    const renderTabLabel = (labelKey, count) => (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            justifyContent="center"
                        >
                            <span>{t(labelKey)}</span>
                            <Chip
                                size="small"
                                label={count}
                                color={count > 0 ? 'primary' : 'default'}
                                variant={count > 0 ? 'filled' : 'outlined'}
                                sx={TAB_COUNT_CHIP_SX}
                            />
                        </Stack>
                    );
                    return (
                        <Box key={sectionId} sx={SECTION_CARD_SX}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ px: 0.5, mb: isExpanded ? 2 : 0, cursor: 'pointer' }}
                                onClick={() => toggleSection(sectionId)}
                            >
                                {section.ticketType?.color && (
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            bgcolor: normalizeHexColor(section.ticketType.color),
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1,
                                        flex: 1,
                                    }}
                                >
                                    {section.ticketType?.name || '—'}
                                </Typography>
                                <IconButton size="small">
                                    {isExpanded ? (
                                        <KeyboardArrowUpIcon />
                                    ) : (
                                        <KeyboardArrowDownIcon />
                                    )}
                                </IconButton>
                            </Stack>
                            <Collapse in={isExpanded}>
                                <Box>
                                    <Tabs
                                        value={currentTab}
                                        onChange={(_, value) => setSectionTabFor(sectionId, value)}
                                        variant="fullWidth"
                                        sx={SECTION_TABS_SX}
                                    >
                                        <Tab
                                            value="unassigned"
                                            label={renderTabLabel(
                                                'timeslots.assignment.tabUnassigned',
                                                unassignedPax,
                                            )}
                                        />
                                        <Tab
                                            value="assigned"
                                            label={renderTabLabel(
                                                'timeslots.assignment.tabAssigned',
                                                assignedPax,
                                            )}
                                        />
                                    </Tabs>
                                    {currentTab === 'unassigned' && (
                                        <Stack spacing={1.5}>
                                            {sortGroups(unassignedInSection).map((group) =>
                                                renderRow(group, 'unassigned'),
                                            )}
                                        </Stack>
                                    )}
                                    {currentTab === 'assigned' &&
                                        tableEntries.map((tg) => (
                                            <Box key={tg.tableId}>
                                                <Typography sx={TABLE_LABEL_SX}>
                                                    {tg.tableName ||
                                                        t('timeslots.assignment.tableUnknown')}
                                                </Typography>
                                                <Stack spacing={1.5}>
                                                    {sortEntries(tg.entries).map(
                                                        ({ group, count, isSplit }) => {
                                                            const showMismatchIcon =
                                                                !seenBookingsInAssigned.has(
                                                                    group.bookingId,
                                                                );
                                                            seenBookingsInAssigned.add(
                                                                group.bookingId,
                                                            );
                                                            return renderRow(
                                                                group,
                                                                'assignedTable',
                                                                count,
                                                                isSplit,
                                                                tg.tableId,
                                                                showMismatchIcon,
                                                            );
                                                        },
                                                    )}
                                                </Stack>
                                            </Box>
                                        ))}
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export default BookingGroupList;
